import { effectsSectionDefaults } from './effects/defaults';
import { healthRulesDefaults } from './health/defaults';
import { namesDefaults } from './names/types';
import { basesHbDefaults, hbSectionDefaults } from './hb/defaults';
import type { BasesHbFields } from './hb/types';
import { panelRulesDefaults, storyRulesDefaults } from './storyDefaults';
import { s2SectionDefaults } from './s2/defaults';
import type { Schemas } from '@argzombie/api-client';
import type {
  BossAttackDef,
  BossDef,
  BossPhaseDef,
  BossPlateDef,
  ConditionCureDef,
  ConditionDef,
  ConditionsDef,
  CosmeticDef,
  MissionDef,
  SquadAiDef,
  SquadRulesDef,
  TriggerCurrencyBlock,
  TriggerDef,
  TriggerEncounterBlock,
  TriggerEventBlock,
  TriggerGeofence,
  TriggerHealthBlock,
  TriggerItemsBlock,
  TriggerRaidBlock,
  TriggerRulesDef,
  CharactersRulesDef,
  ResurrectionBandDef,
  ResurrectionKitDef,
  ResurrectionRulesDef,
  StoreProductDef,
} from './validate';
import { s3SectionDefaults } from './s3/defaults';
import {
  CONDITIONS_WIND_DEFAULTS,
  RESURRECTION_KIT_WIND_DEFAULTS,
  SQUAD_AI_WIND_DEFAULTS,
  SQUAD_WIND_DEFAULTS,
} from './wind/defaults';

/*
 * WIND (ADR 0073, contract 3.7): the wind fields these blocks gained, typed from the generated client until the local
 * lib/validate types (stream W1b) carry them.
 */
export type SquadAiWind = Pick<
  Schemas['SquadAiDef'],
  'fallBackAtTier' | 'clutchAtTier' | 'hurtWindowS'
>;
export type SquadRulesWind = Pick<
  Schemas['SquadRulesDef'],
  'downedWoundsToDie' | 'reviveTier' | 'reviveWindFrac'
>;
export type BossAttackWind = Pick<Schemas['BossAttackDef'], 'wounds'>;
export type ResurrectionKitWind = { fullyRested?: boolean; restTier?: string };
import { s4SectionDefaults } from './s4/defaults';

/**
 * The C# defaults of the Teams on the Map content blocks (packages/shared-cs/Runtime/Content: TeamRulesDef,
 * SurvivorTraitDef, DiscoveryDef, ContainerDefs, EncounterDefs, WorldEventDefs, StoreDefs, SquadRulesDef, BaseRulesDef) and of a boss
 * (BossDef). The server
 * parses a bundle into those classes, so a block or field missing from the JSON gets these values there. The client
 * validator fills the same defaults before checking, and the section editor shows them for a block an old draft
 * doesn't have yet. Keep them in sync with the C# initializers.
 */

export const teamRulesDefaults = (): Schemas['TeamRulesDef'] => ({
  maxTeams: 3,
  slotsAtStart: 2,
  thirdSlotAtRoster: 4,
  maxMembersPerTeam: 4,
  memberCarryKg: 5,
  energyCap: 100,
  energyFloor: -30,
  startEnergy: 100,
  travelSpeedKmh: 5,
  detourFactor: 1.3,
  travelEnergyPerKm: 20,
  raidEnergyCost: 15,
  inPersonEnergyCost: 0,
  inPersonBonusLootRolls: 1,
  regenFieldPerHour: 8,
  regenHomePerHour: 15,
  regenInfirmaryPerLevelPerHour: 5,
  rejoinSpeedKmh: 5,
  rejoinMaxMinutes: 10,
  rejoinEnergyCost: 5,
  reelInDistanceM: 2000,
  tutorialTravelSeconds: 60,
  recruitMaxHealth: 100,
  recruitDailyHeal: 20,
  fatigueTiers: [],
});

export const fatigueTierDefaults = (): Schemas['FatigueTierDef'] => ({
  id: '',
  minEnergy: 0,
  maxHealthMult: 1,
  sprintMult: 1,
  moveNoiseMult: 1,
  searchTimeMult: 1,
  hordeThresholdMult: 1,
});

export const survivorTraitDefaults = (): Schemas['SurvivorTraitDef'] => ({
  id: '',
  name: '',
  description: '',
  travelSpeedMult: 1,
  carryKg: 0,
  moveNoiseMult: 1,
  maxHealthBonus: 0,
});

export const dropRulesDefaults = (): Schemas['DropRulesDef'] => ({
  categoryId: 'supply_cache',
  lifetimeDays: 7,
  maxPerCell: 2,
  sizeM: 14,
  safeTags: [],
  excludeTags: [],
  excludeDistanceM: 30,
  syntheticMinM: 300,
  syntheticMaxM: 1500,
});

export const tutorialDropDefaults = (): Schemas['TutorialDropDef'] => ({
  minDistanceM: 150,
  maxDistanceM: 400,
  expiresHours: 48,
  roomCount: 3,
  difficulty: 1,
  lootTableId: 'supply_cache',
});

export const densityTierDefaults = (): Schemas['DensityTierDef'] => ({
  id: '',
  minBuildings: 0,
  raidStartRadiusM: 100,
});

export const discoveryDefaults = (): Schemas['DiscoveryDef'] => ({
  destinationCount: 12,
  radiusM: 1500,
  sparseRadiusM: 3000,
  minRealBeforeWiden: 8,
  minDistanceM: 150,
  regenerateMovedM: 500,
  regenerateHours: 24,
  categoryMix: [],
  tiers: [],
  drops: dropRulesDefaults(),
  tutorial: tutorialDropDefaults(),
  importRefreshDays: 90,
  importMinIntervalSeconds: 10,
  unsafeReportsToHide: 3,
});

export const containerDefaults = (): Schemas['ContainerDef'] => ({
  anchorMaxKmh: 250,
  anchorJumpM: 2000,
  relocateStableMinutes: 10,
  relocateStableRadiusM: 300,
  relocateMinPings: 3,
  relocateMaxKmh: 900,
  maxRelocationsPerDay: 2,
});

export const convoyDefaults = (): Schemas['ConvoyDef'] => ({
  startKmh: 20,
  stopKmh: 10,
  stopMinutes: 2,
  staleMinutes: 30,
  passedCorridorM: 150,
  maxPassedIds: 200,
  maxQueuedOrders: 3,
  orderExpiresHours: 6,
});

/** shared-cs `BaseRulesDef` (homebase, camps (player-facing: outposts, ADR 0061) and the check-in ledger, ADR 0030). `checkInRadiusM` 0 = `survival.homeCheckInRadiusM`. */
export const basesDefaults = (): Schemas['BaseRulesDef'] & BasesHbFields => ({
  maxActiveCamps: 2,
  campMinDistanceFromHomeM: 2000,
  campMinDistanceBetweenM: 2000,
  campDropCooldownHours: 24,
  maxCampDropsPer30Days: 4,
  checkInRadiusM: 0,
  checkInMinGapHours: 4,
  homeMoveCooldownDays: 7,
  initialHomeGraceHours: 24,
  dropMaxAccuracyM: 50,
  dropAfterRelocationMinutes: 30,
  promoteMinCheckIns: 6,
  promoteMinDistinctDays: 3,
  promoteMinSpanHours: 48,
  promoteMinSessions: 2,
  campExpiryDays: 14, // ADR 0061: outposts last 14 days without a check-in
  campExpiryWarningHours: 24,
  campRegenMult: 0.75,
  caravanMinHours: 1,
  caravanMaxHours: 24,
  caravanHoursPer100Km: 2,
  ...basesHbDefaults(),
});

export const encountersDefaults = (): Schemas['EncountersDef'] => ({
  chancePerKm: 0.15,
  maxPerTrip: 1,
  minTripM: 300,
  atFractionMin: 0.25,
  atFractionMax: 0.75,
  types: [],
});

export const encounterTypeDefaults = (): Schemas['EncounterTypeDef'] => ({
  id: '',
  weight: 0,
  choices: [],
  defaultChoice: null,
  lootTableId: null,
  rollsMin: 0,
  rollsMax: 0,
  detourMinutes: 0,
  pushEnergy: 0,
  timeSavedFraction: 0,
  fleeHealth: 0,
  fleeEnergy: 0,
  roomCount: 0,
  difficultyBonus: 0,
  lootRolls: 0,
  traitChance: 0,
  offerItemIds: [],
  offerQuantity: {},
  wantCategories: [],
});

export const worldEventsDefaults = (): Schemas['WorldEventsDef'] => ({
  epochHours: 6,
  geohashPrecision: 6,
  types: [],
});

export const worldEventTypeDefaults = (): Schemas['WorldEventTypeDef'] => ({
  id: '',
  chancePerCellEpoch: 0,
  everyNthEpoch: 1,
  durationMinutes: 0,
  fullClaims: 0,
  lootTableId: null,
  fullLootRolls: 0,
  lateLootRolls: 0,
  hp: 0,
  damagePerClear: 0,
  difficultyBonus: 0,
  hordeThresholdMult: 1,
  crateLootTableId: null,
  crateLootRolls: 0,
  remoteRecruitChance: 0,
});

export const storeDefaults = (): Schemas['StoreDef'] => ({
  dailyBoughtEnergyCap: 150,
  adRewardEnergy: 30,
  adDailyLimit: 3,
  products: [],
});

export const storeProductDefaults = (): StoreProductDef => ({
  id: '',
  kind: '',
  energy: 0,
  regenMult: 1,
  cosmeticIds: [],
  appleProductIds: {},
  googleProductId: '',
  googleBasePlanId: null,
  tier: null,
});

/** shared-cs `CharactersRulesDef` (ADR 0069 C7): 4 slots, 8 with the VIP pass. */
export const charactersDefaults = (): CharactersRulesDef => ({
  maxSlots: 4,
  vipSlots: 8,
  deleteConfirm: 'name',
  friendshipsAllCharacters: true,
});

/** shared-cs `ResurrectionKitDef` (ADR 0069 C4, the moderate kit). */
export const resurrectionKitDefaults = (): ResurrectionKitDef & ResurrectionKitWind => ({
  foodDays: 3,
  waterDays: 3,
  magazinesForBestGun: 2,
  weaponFromLootTier: true,
  bandages: 2,
  fullHealth: true,
  clearConditions: true,
  ...RESURRECTION_KIT_WIND_DEFAULTS,
});

/** shared-cs `ResurrectionRulesDef.DefaultBands()`: what empty `bands` means on the server (0-2, 3-6, ... 240+ days → tiers 1..8). */
export const DEFAULT_RESURRECTION_BANDS: readonly Required<ResurrectionBandDef>[] = [
  { minDays: 0, tier: 1 },
  { minDays: 3, tier: 2 },
  { minDays: 7, tier: 3 },
  { minDays: 14, tier: 4 },
  { minDays: 30, tier: 5 },
  { minDays: 60, tier: 6 },
  { minDays: 120, tier: 7 },
  { minDays: 240, tier: 8 },
];

/** shared-cs `ResurrectionRulesDef` (ADR 0069 C1-C4). */
export const resurrectionDefaults = (): ResurrectionRulesDef => ({
  bands: [],
  kit: resurrectionKitDefaults(),
  showCutsceneOnce: true,
});

/** The boss statuses (shared-cs `BossStatuses.All`, same order). */
export const BOSS_STATUSES = ['live', 'coming_soon', 'retired'] as const;

/** shared-cs `BossDef` defaults (ADR 0028): what the server gives a boss field the JSON doesn't have. */
export const bossDefaults = (): BossDef => ({
  id: '',
  slug: '',
  codePrefix: '',
  name: '',
  tagline: '',
  lore: '',
  difficulty: 1,
  challenge: 10,
  recommendedTeam: 1,
  status: 'coming_soon',
  playable: false,
  arrivesLabel: '',
  artKey: '',
  rewardsSummary: '',
  trophyId: '',
  trophyName: '',
  energyCost: 25,
  cooldownHours: 20,
  maxHealth: 0,
  headshotMult: 1.5,
  stealthOpenerFrac: 0.12,
  arenaRooms: 2,
  cacheLootTableId: '',
  cacheLootRolls: 0,
  minClearSeconds: 60,
  firstClearRewards: [],
  repeatLootTableId: '',
  repeatRolls: 0,
  arenaId: '',
  antechamberZombieIds: [],
  modelScale: 1.25,
  moveSpeedMps: 1.6,
  sightRangeM: 8,
  sightHalfAngleDeg: 40,
  hearingMult: 1,
  lureMult: 1,
  knockbackResist: 0.9,
  weakSpotMult: 1,
  weakSpotArcDeg: 0,
  exposedMult: 1,
  stealthStaggerS: 1.5,
  plates: [],
  phases: [],
  attacks: [],
  speakerCount: 0,
  speakerHealth: 0,
  speakerRingS: 15,
  speakerHordeFrac: 0.15,
});

/** The boss arenas (shared-cs `BossArenaIds.All`, same order). */
export const BOSS_ARENA_IDS = ['chapel', 'site', 'depot'] as const;
/** Boss plate zones (shared-cs `BossPlateZones.All`, same order). */
export const BOSS_PLATE_ZONES = ['head', 'front'] as const;
/** Boss attack shapes (shared-cs `BossAttackKinds.All`, same order). */
export const BOSS_ATTACK_KINDS = [
  'lunge',
  'scream',
  'charge',
  'sweep',
  'throw',
  'spit',
  'grab',
] as const;

/** shared-cs `BossPlateDef` defaults. */
export const bossPlateDefaults = (): BossPlateDef => ({
  id: '',
  name: '',
  zone: 'front',
  health: 0,
});

/** shared-cs `BossPhaseDef` defaults. */
export const bossPhaseDefaults = (): BossPhaseDef => ({
  id: '',
  banner: '',
  atHealthFrac: 0,
  plateBroken: '',
  afterSpeakers: false,
  attacks: [],
  speedMult: 1,
  shielded: false,
  addsZombieId: '',
  addsPerWave: 0,
  addsIntervalS: 0,
  addsOnNoiseM: 0,
  addsOnNoiseCooldownS: 3,
  addsMax: 0,
  visionRadiusM: 0,
  channelS: 0,
  channelInterruptDamage: 0,
  channelHordePerS: 0,
  channelCooldownS: 12,
  timerS: 0,
  timerHealFrac: 0,
  hordeForced: false,
});

/** shared-cs `BossAttackDef` defaults. */
export const bossAttackDefaults = (): BossAttackDef & BossAttackWind => ({
  id: '',
  word: '',
  kind: 'lunge',
  windupS: 1,
  rangeM: 3,
  lengthM: 0,
  widthM: 1.2,
  radiusM: 0,
  arcDeg: 0,
  count: 1,
  damage: 0,
  stunS: 0,
  knockbackM: 0,
  cooldownS: 5,
  speedMps: 0,
  wallStunS: 0,
  puddleS: 0,
  puddleDps: 0,
  slowS: 0,
  slowMult: 1,
  coverNegates: false,
  usesBiteBlock: false,
  wounds: false,
});

/** shared-cs `SquadAiDef` (squad raids, ADR 0027). */
export const squadAiDefaults = (): SquadAiDef & SquadAiWind => ({
  thinkHz: 5,
  acquireMinS: 0.4,
  acquireMaxS: 0.6,
  firstShotWindowS: 0.6,
  fireIntervalMult: 1.25,
  hitChanceNear: 0.6,
  nearM: 4,
  hitChanceFar: 0.25,
  farM: 10,
  headshotChance: 0.08,
  retreatHealthFrac: 0.35,
  killShareSoftCap: 0.4,
  killShareHardCap: 0.45,
  zombieTargetWeight: 1.25,
  downedTargetWeight: 0.3,
  biteGraceS: 1.5,
  noiseTaxPerFollower: 0.15,
  scoreChasingPlayer: 50,
  scoreNearSquad: 30,
  nearSquadM: 3,
  scoreInSector: 20,
  scoreRunner: 15,
  scoreBrute: -15,
  scorePlayersTarget: -30,
  playersTargetMinM: 2.5,
  scoreShared: -15,
  scorePerMeter: -2,
  meleeHitChance: 0.8,
  clutchHitChance: 0.9,
  clutchShots: 2,
  clutchCooldownS: 8,
  clutchPlayerHealthFrac: 0.3,
  clutchRangeM: 2,
  lineOfFireRadiusM: 0.4,
  lineOfFirePastM: 1,
  reloadBelowFrac: 0.4,
  reloadIdleS: 2,
  killShareWindowS: 60,
  killShareMinKills: 4,
  softCapHitMult: 0.6,
  softCapAcquireAddS: 0.4,
  hardCapRangeM: 3,
  struggleShare: 0.15,
  struggleHitMult: 1.25,
  struggleWindowS: 10,
  weaponsFreeNoiseM: 12,
  stealthRevertS: 6,
  fileSpacingM: 1.2,
  fileSprintSpacingM: 1.6,
  fileCrouchSpacingM: 0.9,
  fileFirstM: 1.4,
  fanBearingDeg: 140,
  fanSideM: 1.8,
  fanRearM: 2.8,
  narrowWidthM: 3,
  doorwayM: 1.5,
  combatTighten: 0.8,
  slotToleranceM: 0.75,
  slotSwitchGain: 0.2,
  noGoHalfAngleDeg: 30,
  noGoRangeM: 6,
  minSpacingM: 0.9,
  matchNearM: 3,
  catchUpFarM: 7,
  catchUpMult: 1.25,
  sprintCatchUpMult: 1.15,
  crouchCatchUpMult: 1.3,
  teleportPathM: 12,
  teleportAfterS: 3,
  teleportCooldownS: 10,
  teleportBackMinM: 4,
  teleportBackMaxM: 8,
  attackLostS: 4,
  regroupIgnoreM: 2,
  invisibleFollowM: 6,
  rescueCooldownS: 45,
  autoReviveClearM: 6,
  orderTimeScale: 0.35,
  orderSlowMoCapS: 3,
  orderSlowMoRampS: 0.25,
  orderSlowMoCooldownS: 1,
  stickinessM: 1.5,
  teammateAttackers: 2,
  playerAttackersCovered: 2,
  playerAttackersAlone: 3,
  teammateNearM: 5,
  holdOffM: 1.5,
  offscreenWindUpS: 0.3,
  biteStaggerM: 1,
  fallBackFightM: 3,
  stackWallOffsetM: 0.5,
  stackFirstM: 0.6,
  stackSpacingM: 0.8,
  entryIntervalS: 0.35,
  entryHoldS: 0.8,
  entryPointMinM: 1.5,
  entryPointMaxM: 2.2,
  entryTimeoutS: 8,
  clearQuietS: 1.5,
  openNoiseM: 3,
  kickNoiseM: 20,
  kickStaggerRadiusM: 2,
  kickStaggerS: 1,
  barkGapS: 1.5,
  plainAcks: 3,
  contactBarkCooldownS: 8,
  reloadBarkCooldownS: 6,
  killBarkChance: 0.2,
  killBarkCooldownS: 10,
  idleBarkCooldownS: 45,
  peekS: 1.5,
  // Combat HUD overhaul (B1, B2).
  defaultTeamDisposition: 'clearHot',
  holdAvoidM: 4,
  silentTakedownMaxM: 8,
  huntSweptSeenFrac: 0.8,
  huntForceLockedDoors: true,
  huntLiftsKillCap: true,
  huntHomeInOnLast: true,
  huntRepathS: 1,
  huntGiveUpS: 30,
  huntAreaClearToastS: 2.5,
  ...SQUAD_AI_WIND_DEFAULTS,
});

/** shared-cs `SquadRulesDef` (squad raids, ADR 0027). */
export const squadRulesDefaults = (): SquadRulesDef & SquadRulesWind => ({
  raidEnergyPerExtraMember: 3,
  inPersonEnergyPerExtraMember: 2,
  travelEnergyPerKmPerExtraMember: 0,
  zombieMultPerExtraMember: 0.15,
  maxLiveZombiesPerExtraMember: 1,
  hordeThresholdPerExtraMember: 0,
  largeRoomsAtSquadSize: 3,
  largeRoomMinSide: 5,
  largeRoomAverageArea: 36,
  rationItemIds: [],
  rationsPerExtraMember: 0,
  downedBleedOutS: 20,
  downedBitesToDie: 2,
  reviveSeconds: 3,
  reviveRangeM: 1.2,
  reviveHealthFrac: 0.3,
  revivesPerMember: 1,
  remoteHealM: 8,
  ...SQUAD_WIND_DEFAULTS,
  ai: squadAiDefaults(),
});

/*
 * Scan triggers (ADR 0029): the C# initializers of shared-cs `TriggerDef`, `TriggerRules` and the blocks
 * (Runtime/Content/TriggerDefs.cs), `MissionDef` and `CosmeticDef`.
 */
export const triggerRulesDefaults = (): TriggerRulesDef => ({
  scope: 'run',
  limit: 'once',
  cooldownHours: 0,
  maxPerPlayer: null,
  maxTotal: null,
  activeFrom: null,
  activeUntil: null,
  season: null,
  geofence: null,
  requiresLiveRun: true,
  requiresApp: true,
});

export const triggerDefaults = (): TriggerDef => ({
  id: '',
  action: '',
  payloadVersion: 1,
  title: '',
  description: '',
  art: '',
  codePrefix: '',
  status: 'coming_soon',
  missionId: null,
  rewardsSummary: '',
  devOnly: false,
  rules: triggerRulesDefaults(),
  encounter: null,
  items: null,
  health: null,
  currency: null,
  cosmetic: null,
  mission: null,
  event: null,
});

export const triggerGeofenceDefaults = (): TriggerGeofence => ({ lat: 0, lon: 0, radiusM: 0 });
export const triggerEncounterDefaults = (): TriggerEncounterBlock => ({
  kind: '',
  bossId: null,
  encounterId: null,
  raid: null,
});
export const triggerRaidDefaults = (): TriggerRaidBlock => ({
  category: '',
  difficulty: 1,
  lootTableId: '',
  lootRolls: 0,
  rooms: 4,
  zombieIds: [],
});
export const triggerItemsDefaults = (): TriggerItemsBlock => ({ stacks: [], destination: 'home' });
export const triggerHealthDefaults = (): TriggerHealthBlock => ({ amount: 0, target: 'player' });
export const triggerCurrencyDefaults = (): TriggerCurrencyBlock => ({
  kind: 'energy_reserve',
  amount: 0,
});
export const triggerEventDefaults = (): TriggerEventBlock => ({ type: '', maxDistanceM: 800 });

export const missionDefaults = (): MissionDef => ({
  id: '',
  title: '',
  kicker: '',
  briefing: '',
  art: '',
  triggerId: '',
  order: 0,
  visibility: 'listed',
  status: 'coming_soon',
  scope: 'run',
  rewardsSummary: '',
  devOnly: false,
});

export const cosmeticDefaults = (): CosmeticDef => ({ id: '', name: '', kind: '' });

/** shared-cs `ConditionsDef` (survivor conditions, ADR 0031): off with no types in a bundle that predates it. */
export const conditionsDefaults = (): ConditionsDef => ({
  enabled: false,
  // medicCut (C# default 0.33) is left out: card 8 retired it with the roles, so the form doesn't write it back.
  infirmaryCutPerLevel: 0.33,
  minDurationMult: 0.5,
  minMaxHealthMult: 0.5,
  woundedBelowHealthFrac: 0.3,
  laidUpBelowHealthFrac: 0.1,
  starvingDaysToLayUp: 2,
  shortageSickChance: 0.5,
  riskySickChance: 0.35,
  biteChancePer10Damage: 0.02,
  maxBiteChance: 0.6,
  playerTurnedMaxHealthMult: 0.5,
  ...CONDITIONS_WIND_DEFAULTS,
  types: [],
});

/** shared-cs `ConditionDef` (one condition type). */
export const conditionTypeDefaults = (): ConditionDef => ({
  id: '',
  name: '',
  description: '',
  severity: 0,
  homeHours: 0,
  awayHours: 0,
  maxHealthMult: 1,
  windCapacityMult: 1,
  windRecoveryMult: 1,
  sprintMult: 1,
  moveNoiseMult: 1,
  regenMult: 1,
  sitsOut: false,
  turnHours: 0,
  cures: [],
});

/** shared-cs `ConditionCureDef`. */
export const conditionCureDefaults = (): ConditionCureDef => ({
  itemId: '',
  moduleId: null,
  moduleLevel: 0,
});

/** The object blocks, by bundle key: what the server uses when a bundle doesn't have the block. */
export const teamsSectionDefaults = {
  bases: basesDefaults,
  teamRules: teamRulesDefaults,
  discovery: discoveryDefaults,
  container: containerDefaults,
  convoy: convoyDefaults,
  encounters: encountersDefaults,
  worldEvents: worldEventsDefaults,
  store: storeDefaults,
  characters: charactersDefaults,
  resurrection: resurrectionDefaults,
  squadRules: squadRulesDefaults,
  conditions: conditionsDefaults,
  story: storyRulesDefaults,
  panelRules: panelRulesDefaults,
  ...s2SectionDefaults,
  ...s3SectionDefaults,
  ...s4SectionDefaults,
  ...hbSectionDefaults,
  ...effectsSectionDefaults,
  healthRules: healthRulesDefaults,
  names: namesDefaults,
} as const;

/**
 * `value` with the C# defaults filled in for keys the JSON doesn't have (like the server's parser). An explicit `null`
 * stays `null`, so the validator can report it the way the server does. `null`/`undefined` input is returned as is.
 */
export function withDefaults<T extends object>(defaults: T, value: T): T;
export function withDefaults<T extends object>(
  defaults: T,
  value: T | null | undefined,
): T | null | undefined;
export function withDefaults<T extends object>(defaults: T, value: T | null | undefined) {
  if (value == null) return value;
  const present = Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined));
  return { ...defaults, ...present } as T;
}

/** An object block as the server sees it: the defaults when the key is absent, `null` when it's explicitly null. */
export function blockAsParsed<T extends object>(defaults: () => T, value: T | null | undefined) {
  return value === undefined ? defaults() : withDefaults(defaults(), value);
}
